import React from "react";

import {
  createQColumnHelper,
  QBodyLayout,
  QBox,
  QButton,
  QDataTable,
  QEmptyState,
  QEmptyStateButtonGroup,
  QEmptyStatePrimaryButton,
  QPageLoader,
} from "@qualio/ui-components";
import type { TagStatusMapping } from "@qualio/ui-components/lib/QOrganisms/Table/Cells/types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Document,
  DocumentStatus,
  DocumentStatusIdToStatusConfigMap,
} from "../../types/document";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";
import { DocumentsDataProvider } from "./DocumentsDataProvider";
import { DocumentSearchControls } from "./DocumentSearchControls";
import { useTags } from "../../hooks/useTags";

export const Documents: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tags: productTags } = useCurrentProduct();
  const { data: tags, isLoading: isLoadingTags } = useTags();

  // NOTE: If/when CL is bumped to >= 30, we can move this to the Fixed data provider `isLoading` prop.
  if (isLoadingTags) {
    return <QPageLoader />;
  }

  if (!tags || tags.length === 0 || !productTags || productTags.length === 0) {
    return (
      <QEmptyState
        data-cy="documents-empty-state"
        title={t("documents.emptyState.title")}
        subtitle={t("documents.emptyState.subtitle")}
      >
        <QEmptyStateButtonGroup>
          <QEmptyStatePrimaryButton>
            <QButton onClick={() => navigate(`../config/`)}>
              {t("documents.emptyState.actions.addTag")}
            </QButton>
          </QEmptyStatePrimaryButton>
        </QEmptyStateButtonGroup>
      </QEmptyState>
    );
  }

  const COLUMNS = getColumns(t);

  return (
    <DocumentsDataProvider allTags={tags} productTagIds={productTags}>
      <QBox
        sx={{
          // NOTE: This is a fix for a CSS clash for <p> tags within a #design-controls-container.
          "& p": {
            marginTop: "0px !important",
            marginBottom: "0px !important",
          },
        }}
      >
        <QBodyLayout.Default>
          <DocumentSearchControls allTags={tags} productTagIds={productTags} />
          <QDataTable columns={COLUMNS} />
        </QBodyLayout.Default>
      </QBox>
    </DocumentsDataProvider>
  );
};

const columnHelper = createQColumnHelper<Document>();

const getColumns = (t: (key: string) => string) => [
  columnHelper.text("code", { header: t("documents.table.header.id") }),
  columnHelper.textLink(
    "title",
    ({ id }) => `${window.location.origin}/workspace/documents/${id}`,
    {
      header: t("documents.table.header.title"),
      isExternal: true,
    },
  ),
  columnHelper.text("type", { header: t("documents.table.header.type") }),
  columnHelper.text("owner", { header: t("documents.table.header.owner") }),
  columnHelper.tag(
    (document: Document) => document.tag_ids.map((tag) => tag.toString()),
    {
      id: "tag_ids",
      header: t("documents.table.header.tags"),
      statuses: { "*": "default" },
      enableSorting: false,
    },
  ),
  columnHelper.text("version", { header: t("documents.table.header.version") }),
  columnHelper.status(
    (document) =>
      DocumentStatusIdToStatusConfigMap[document.status_id as DocumentStatus]
        .label,
    {
      id: "status_id",
      header: t("documents.table.header.status"),
      width: "140px",
      statuses: Object.values(DocumentStatusIdToStatusConfigMap).reduce(
        (acc: TagStatusMapping, statusConfig) => {
          acc[statusConfig.label] = statusConfig.color;
          return acc;
        },
        {},
      ),
    },
  ),
];
